import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface HolidayThemeContextType {
  isHolidaySeason: boolean;
  holidayType: 'christmas' | 'newyear' | null;
  isManuallyEnabled: boolean;
  setIsManuallyEnabled: (enabled: boolean) => void;
  enableMusic: boolean;
  setEnableMusic: (enabled: boolean) => void;
  enableAnimations: boolean;
  setEnableAnimations: (enabled: boolean) => void;
}

const HolidayThemeContext = createContext<HolidayThemeContextType | undefined>(undefined);

export const useHolidayTheme = () => {
  const context = useContext(HolidayThemeContext);
  if (!context) {
    throw new Error('useHolidayTheme must be used within HolidayThemeProvider');
  }
  return context;
};

interface HolidayThemeProviderProps {
  children: ReactNode;
}

export const HolidayThemeProvider: React.FC<HolidayThemeProviderProps> = ({ children }) => {
  const [isHolidaySeason, setIsHolidaySeason] = useState(false);
  const [holidayType, setHolidayType] = useState<'christmas' | 'newyear' | null>(null);
  const [isManuallyEnabled, setIsManuallyEnabledState] = useState<boolean | null>(null);
  const [enableMusic, setEnableMusic] = useState(false);
  const [enableAnimations, setEnableAnimations] = useState(true);

  // Load manual override from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('holiday-theme-manual');
    if (saved !== null) {
      setIsManuallyEnabledState(saved === 'true');
    } else {
      setIsManuallyEnabled(true)
    }
  }, []);

  // Save manual override to localStorage
  const setIsManuallyEnabled = (enabled: boolean) => {
    setIsManuallyEnabledState(enabled);
    localStorage.setItem('holiday-theme-manual', enabled.toString());
  };

  useEffect(() => {
    const checkHolidaySeason = () => {
      // Check if manually enabled/disabled
      if (isManuallyEnabled !== null) {
        if (isManuallyEnabled) {
          // If manually enabled, determine which holiday type
          const urlParams = new URLSearchParams(window.location.search);
          const testHoliday = urlParams.get('holiday') || localStorage.getItem('holiday-theme-test');
          
          if (testHoliday === 'christmas' || testHoliday === 'newyear') {
            setIsHolidaySeason(true);
            setHolidayType(testHoliday);
            return;
          }

          // Default to Christmas if manually enabled without specific type
          const now = new Date();
          const month = now.getMonth();
          const day = now.getDate();

          if (month === 11 && day >= 30) {
            setIsHolidaySeason(true);
            setHolidayType('newyear');
          } else if (month === 0 && day <= 2) {
            setIsHolidaySeason(true);
            setHolidayType('newyear');
          } else {
            setIsHolidaySeason(true);
            setHolidayType('christmas');
          }
          return;
        } else {
          // Manually disabled
          setIsHolidaySeason(false);
          setHolidayType(null);
          return;
        }
      }

      // Auto-detect based on date
      const now = new Date();
      const month = now.getMonth(); // 0-11
      const day = now.getDate();

      // Christmas season: December 1 - December 31
      if (month === 11) {
        setIsHolidaySeason(true);
        setHolidayType('christmas');
        return;
      }

      // New Year: December 30 - January 2
      if (month === 11 && day >= 30) {
        setIsHolidaySeason(true);
        setHolidayType('newyear');
        return;
      }

      if (month === 0 && day <= 2) {
        setIsHolidaySeason(true);
        setHolidayType('newyear');
        return;
      }

      setIsHolidaySeason(false);
      setHolidayType(null);
    };

    checkHolidaySeason();
    // Check daily
    const interval = setInterval(checkHolidaySeason, 1000 * 60 * 60); // Every hour
    return () => clearInterval(interval);
  }, [isManuallyEnabled]);

  return (
    <HolidayThemeContext.Provider
      value={{
        isHolidaySeason,
        holidayType,
        isManuallyEnabled: isManuallyEnabled ?? false,
        setIsManuallyEnabled,
        enableMusic,
        setEnableMusic,
        enableAnimations,
        setEnableAnimations,
      }}
    >
      {children}
    </HolidayThemeContext.Provider>
  );
};

